import { useEffect, useState } from 'react'
import { Image as ImageIcon } from 'lucide-react'
import { mediaApi } from '@/api/media'
import type { JobMediaItem, MediaPhase } from '@/types/domain'

const PHASES: MediaPhase[] = ['Before', 'After']

function Thumbnail({ mediaId, roomTag }: { mediaId: string; roomTag: string | null }) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    mediaApi.fetchFile(mediaId).then((blob) => {
      objectUrl = URL.createObjectURL(blob)
      setUrl(objectUrl)
    }).catch(() => {})
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [mediaId])

  return (
    <a href={url ?? undefined} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700">
      {url ? (
        // eslint-disable-next-line jsx-a11y/alt-text -- private, auth-fetched blob URL, not an optimizable static asset
        <img src={url} alt={roomTag ?? 'Job photo'} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-gray-300 dark:text-gray-600">
          <ImageIcon className="h-5 w-5" />
        </div>
      )}
    </a>
  )
}

/** Read-only for staff — only the assigned worker can upload (the backend's Upload action
 * resolves the caller to a Worker record, which a manager/admin JWT never has). */
export function JobMediaPanel({ jobId }: { jobId: string }) {
  const [items, setItems] = useState<JobMediaItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [phase, setPhase] = useState<MediaPhase>('Before')

  useEffect(() => {
    setIsLoading(true)
    mediaApi.listForJob(jobId).then(setItems).finally(() => setIsLoading(false))
  }, [jobId])

  const visible = items.filter((i) => i.phase === phase)

  return (
    <div className="mt-2 rounded-lg border border-gray-100 p-3 dark:border-gray-700">
      <div className="mb-2 flex gap-1">
        {PHASES.map((p) => (
          <button
            key={p}
            onClick={() => setPhase(p)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              phase === p ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {p} ({items.filter((i) => i.phase === p).length})
          </button>
        ))}
      </div>
      {isLoading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="text-xs text-gray-400">No {phase.toLowerCase()} photos.</p>
      ) : (
        <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
          {visible.map((item) => (
            <Thumbnail key={item.id} mediaId={item.id} roomTag={item.roomTag} />
          ))}
        </div>
      )}
    </div>
  )
}
